"use client";

import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useState,
} from "react";
import uniqueId from "lodash.uniqueid";

import { NotificationOptions, Notification } from "./types";
import Notifications from "./Notifications";

const DEFAULT_TIMEOUT = 5000;
const DEFAULT_ERROR_TIMEOUT = 10_000;

interface Props {
  children: React.ReactNode;
  notificationLabel: string;
  notificationTimeout?: number;
}

interface NotificationContextType {
  clearNotification: (id: string) => void;
  createNotification: (options: NotificationOptions) => void;
}

const defaultNotificationContext = {
  clearNotification: () => null,
  createNotification: () => null,
};

const NotificationContext = createContext<NotificationContextType>(
  defaultNotificationContext,
);

export function useNotification(): NotificationContextType {
  return useContext(NotificationContext);
}

export function NotificationProvider(props: Props) {
  const { notificationLabel } = props;
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const clearNotification = useCallback((id: string) => {
    setNotifications((previous) => previous.filter((n) => n.id !== id));
  }, []);

  const createNotification = useCallback((options: NotificationOptions) => {
    // for error notifications, if no timeout is explicitly set, don't auto-dismiss
    const timeout =
      options.type === "error" && !options.timeout
        ? DEFAULT_ERROR_TIMEOUT
        : options.timeout;
    setNotifications((previous) => [
      ...previous,
      { ...options, timeout, id: uniqueId("notification-") },
    ]);
  }, []);

  const notificationTimeout = props.notificationTimeout || DEFAULT_TIMEOUT;

  const value = {
    clearNotification,
    createNotification,
  };

  return (
    <Fragment>
      <Notifications
        notifications={notifications}
        onDismiss={clearNotification}
        timeout={notificationTimeout}
        label={notificationLabel}
      />
      <NotificationContext.Provider value={value}>
        {props.children}
      </NotificationContext.Provider>
    </Fragment>
  );
}
